<template>
  <Modal ref="modal" class="send-approval-modal">
    <!-- Passo 1: o que o cliente vai assinar -->
    <template v-if="!created">
      <h2 class="box__title">{{ $t('sendApproval.title') }}</h2>
      <p class="send-approval__intro">{{ $t('sendApproval.intro') }}</p>

      <FormGroup :label="$t('sendApproval.pieceTitle')" required>
        <input
          v-model="title"
          class="input"
          :placeholder="$t('sendApproval.pieceTitlePlaceholder')"
          maxlength="255"
        />
      </FormGroup>

      <FormGroup
        :label="$t('sendApproval.message')"
        :help-text="$t('sendApproval.messageHelp')"
      >
        <textarea
          v-model="message"
          class="input send-approval__textarea"
          rows="4"
          :placeholder="$t('sendApproval.messagePlaceholder')"
          maxlength="10000"
        ></textarea>
      </FormGroup>

      <FormGroup
        :label="$t('sendApproval.files')"
        :help-text="$t('sendApproval.filesHelp')"
      >
        <div v-if="attachmentOptions.length > 0" class="send-approval__files">
          <label
            v-for="option in attachmentOptions"
            :key="option.name"
            class="send-approval__file"
          >
            <Checkbox
              :model-value="selectedFiles.includes(option.name)"
              @update:model-value="toggleFile(option.name)"
            ></Checkbox>
            <i
              :class="option.isImage ? 'iconoir-media-image' : 'iconoir-page'"
            ></i>
            <span>{{ option.visibleName }}</span>
          </label>
        </div>
        <a class="send-approval__upload" @click="$refs.fileInput.click()">
          <i class="iconoir-plus"></i> {{ $t('sendApproval.upload') }}
        </a>
        <input
          ref="fileInput"
          type="file"
          multiple
          class="send-approval__file-input"
          @change="onFilePicked"
        />
      </FormGroup>

      <FormGroup :label="$t('sendApproval.contact')">
        <div v-if="recentContacts.length > 0" class="send-approval__recent">
          <a
            v-for="(contact, index) in recentContacts"
            :key="index"
            class="send-approval__recent-chip"
            @click="applyContact(contact)"
          >
            {{ contact.name || contact.email || contact.phone }}
          </a>
        </div>
        <div class="send-approval__contact-grid">
          <input
            v-model="contactName"
            class="input"
            :placeholder="$t('sendApproval.contactName')"
            maxlength="255"
          />
          <input
            v-model="contactPhone"
            class="input"
            :placeholder="$t('sendApproval.contactPhone')"
            maxlength="64"
          />
          <input
            v-model="contactEmail"
            class="input"
            :placeholder="$t('sendApproval.contactEmail')"
            maxlength="255"
          />
        </div>
      </FormGroup>

      <a class="send-approval__advanced" @click="advanced = !advanced">
        <i
          :class="advanced ? 'iconoir-nav-arrow-down' : 'iconoir-nav-arrow-right'"
        ></i>
        {{ $t('sendApproval.advanced') }}
      </a>
      <template v-if="advanced">
        <FormGroup
          :label="$t('sendApproval.approvers')"
          :help-text="$t('sendApproval.approversHelp')"
        >
          <input
            v-model="approversText"
            class="input"
            :placeholder="$t('sendApproval.approversPlaceholder')"
          />
        </FormGroup>

        <FormGroup
          v-if="statusField"
          :label="$t('sendApproval.statusOnApprove')"
        >
          <Dropdown v-model="statusOptionId" :show-search="false">
            <DropdownItem
              :name="$t('sendApproval.statusKeep')"
              :value="null"
            ></DropdownItem>
            <DropdownItem
              v-for="option in statusField.select_options"
              :key="option.id"
              :name="option.value"
              :value="option.id"
            ></DropdownItem>
          </Dropdown>
        </FormGroup>

        <div class="send-approval__options">
          <Checkbox v-model="remindersEnabled">
            {{ $t('sendApproval.reminders') }}
          </Checkbox>
        </div>
      </template>

      <div class="actions actions--right">
        <Button
          type="primary"
          size="large"
          :loading="saving"
          :disabled="!title.trim() || saving"
          @click="submit"
        >
          {{ $t('sendApproval.create') }}
        </Button>
      </div>
    </template>

    <!-- Passo 2: enviar o link -->
    <template v-else>
      <h2 class="box__title">{{ $t('sendApproval.readyTitle') }}</h2>
      <p class="send-approval__ready-text">
        {{ $t('sendApproval.readyText') }}
      </p>
      <div class="send-approval__link">
        <input
          class="input"
          :value="created.public_url"
          readonly
          @focus="$event.target.select()"
        />
        <Button type="secondary" icon="iconoir-copy" @click="copyLink">
          {{ $t('sendApproval.copy') }}
        </Button>
      </div>
      <div class="send-approval__channels">
        <Button
          type="primary"
          icon="iconoir-chat-bubble"
          @click="openWhatsApp"
        >
          WhatsApp
        </Button>
        <Button
          type="secondary"
          icon="iconoir-mail"
          :loading="sendingEmail"
          :disabled="!created.contact_email || sendingEmail"
          @click="sendEmail"
        >
          {{ $t('sendApproval.byEmail') }}
        </Button>
        <Button type="secondary" @click="hide()">
          {{ $t('sendApproval.done') }}
        </Button>
      </div>
      <div class="send-approval__fingerprint">
        {{ $t('sendApproval.fingerprintNote') }}
        <code>{{ created.versions[0].fingerprint.slice(0, 12) }}…</code>
      </div>
    </template>
  </Modal>
</template>

<script>
import modal from '@baserow/modules/core/mixins/modal'
import { notifyIf } from '@baserow/modules/core/utils/error'
import ApprovalsService from '@baserow/modules/core/services/approvals'
import UserFileService from '@baserow/modules/core/services/userFile'
import { copyToClipboard } from '@baserow/modules/database/utils/clipboard'
import { getStatusField } from '@baserow/modules/database/utils/dueDate'
import { buildApprovalShareMessage } from '@baserow/modules/database/utils/approvalShare'

const DESCRIPTION_FIELD_NAMES = new Set([
  'descrição',
  'descricao',
  'description',
  'detalhes',
  'briefing',
  'observações',
  'observacoes',
])

export default {
  name: 'SendApprovalModal',
  mixins: [modal],
  props: {
    row: {
      type: Object,
      required: true,
    },
    table: {
      type: Object,
      required: true,
    },
    fields: {
      type: Array,
      required: true,
    },
  },
  emits: ['created'],
  data() {
    return {
      title: '',
      message: '',
      uploaded: [],
      selectedFiles: [],
      contactName: '',
      contactPhone: '',
      contactEmail: '',
      approversText: '',
      statusOptionId: null,
      remindersEnabled: true,
      advanced: false,
      recentContacts: [],
      saving: false,
      created: null,
      sendingEmail: false,
    }
  },
  computed: {
    service() {
      return ApprovalsService(this.$client)
    },
    statusField() {
      return getStatusField(this.fields)
    },
    rowAttachments() {
      const files = []
      for (const field of this.fields) {
        if (field.type !== 'file') continue
        const value = this.row[`field_${field.id}`]
        if (!Array.isArray(value)) continue
        for (const file of value) {
          files.push({
            name: file.name,
            visibleName: file.visible_name,
            isImage: !!file.is_image,
          })
        }
      }
      return files
    },
    attachmentOptions() {
      return [...this.rowAttachments, ...this.uploaded]
    },
  },
  methods: {
    show(...args) {
      this.resetForm()
      this.fetchContacts()
      return modal.methods.show.call(this, ...args)
    },
    descriptionText() {
      // Pré-preenche a mensagem com o campo de descrição da tarefa, se houver
      // (texto longo ou rico → texto puro).
      const field = this.fields.find(
        (f) =>
          ['long_text', 'text'].includes(f.type) &&
          DESCRIPTION_FIELD_NAMES.has((f.name || '').trim().toLowerCase())
      )
      if (!field) return ''
      const value = this.row[`field_${field.id}`]
      if (!value || typeof value !== 'string') return ''
      return value.replace(/<[^>]+>/g, '').trim()
    },
    resetForm() {
      const primaryField = this.fields.find((field) => field.primary)
      this.title = primaryField
        ? String(this.row[`field_${primaryField.id}`] || '')
        : ''
      this.message = this.descriptionText()
      this.uploaded = []
      this.selectedFiles = this.rowAttachments.map((file) => file.name)
      this.contactName = ''
      this.contactPhone = ''
      this.contactEmail = ''
      this.approversText = ''
      this.statusOptionId = null
      this.remindersEnabled = true
      this.advanced = false
      this.saving = false
      this.created = null
    },
    async fetchContacts() {
      try {
        const { data } = await this.service.contacts(this.table.id)
        this.recentContacts = data.contacts
      } catch (error) {
        this.recentContacts = []
      }
    },
    applyContact(contact) {
      this.contactName = contact.name || this.contactName
      this.contactPhone = contact.phone || this.contactPhone
      this.contactEmail = contact.email || this.contactEmail
    },
    toggleFile(name) {
      if (this.selectedFiles.includes(name)) {
        this.selectedFiles = this.selectedFiles.filter((n) => n !== name)
      } else {
        this.selectedFiles = [...this.selectedFiles, name]
      }
    },
    async onFilePicked(event) {
      const files = Array.from(event.target.files || [])
      event.target.value = ''
      for (const file of files) {
        try {
          const { data: userFile } = await UserFileService(
            this.$client
          ).uploadFile(file)
          this.uploaded.push({
            name: userFile.name,
            visibleName: file.name,
            isImage: (file.type || '').startsWith('image/'),
          })
          this.selectedFiles = [...this.selectedFiles, userFile.name]
        } catch (error) {
          notifyIf(error, 'sendApproval')
        }
      }
    },
    async submit() {
      this.saving = true
      const files = this.attachmentOptions
        .filter((option) => this.selectedFiles.includes(option.name))
        .map((option) => ({
          name: option.name,
          visible_name: option.visibleName,
        }))
      const approvers = this.approversText
        .split(',')
        .map((name) => name.trim())
        .filter((name) => name.length > 0)
      try {
        const { data } = await this.service.create(this.table.id, this.row.id, {
          title: this.title,
          message: this.message,
          files,
          contact_name: this.contactName,
          contact_phone: this.contactPhone,
          contact_email: this.contactEmail,
          approvers,
          status_field_id:
            this.statusOptionId && this.statusField
              ? this.statusField.id
              : null,
          status_option_id: this.statusOptionId,
          reminders_enabled: this.remindersEnabled,
        })
        this.created = data.approval
        this.$emit('created', data.approval)
      } catch (error) {
        notifyIf(error, 'sendApproval')
      }
      this.saving = false
    },
    copyLink() {
      copyToClipboard(this.created.public_url)
      this.$store.dispatch('toast/info', {
        title: this.$t('rowApprovals.copiedTitle'),
        message: this.$t('rowApprovals.copied'),
      })
    },
    openWhatsApp() {
      const message = buildApprovalShareMessage(this.created)
      const digits = (this.created.contact_phone || '').replace(/\D/g, '')
      const url = digits
        ? `https://wa.me/${digits}?text=${encodeURIComponent(message)}`
        : `https://wa.me/?text=${encodeURIComponent(message)}`
      window.open(url, '_blank', 'noopener')
    },
    async sendEmail() {
      this.sendingEmail = true
      try {
        const { data } = await this.service.sendEmail(
          this.created.id,
          this.created.contact_email
        )
        this.$store.dispatch('toast/info', {
          title: this.$t('rowApprovals.emailSentTitle'),
          message:
            data.sent_via === 'gmail'
              ? this.$t('rowApprovals.emailSentGmail')
              : this.$t('rowApprovals.emailSentSmtp'),
        })
      } catch (error) {
        notifyIf(error, 'sendApproval')
      }
      this.sendingEmail = false
    },
  },
}
</script>
